import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import styled from '@emotion/styled';
import {
  AnnouncementPanel,
  Bar,
  Button,
  Select,
  Typography,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRefreshToken } from '../../../../user-management';
import { useEnvVariables } from '../../../../env-variables-manager';
import {
  downloadSeparateFiles,
  NUMBER_OF_MODULES,
  NUMBER_OF_STRETCHES,
  processDownloadResult,
  type DocumentParams,
  type DownloadProgress,
  type FailedDownload,
} from './download-utils';
import { FailedDownloads } from './failed-downloads';
import {
  documentTypeOptions,
  downloadTypeOptions,
  gradeOptions,
  languageOptions,
  standardsOptions,
  type DocumentType,
  type DownloadType,
  type FormState,
  type Language,
} from './select-options';

const Container = styled.div`
  padding: 32px;
  max-width: 800px;
  margin: 0 auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectRow = styled.div`
  display: flex;
  gap: 24px;
  > div:first-of-type {
    flex: 2;
  }
  > div:last-of-type {
    flex: 1;
  }
`;

const StyledAnnouncementPanel = styled(AnnouncementPanel)`
  margin-bottom: 24px;
`;

const ProgressContainer = styled.div`
  margin-top: 24px;
  padding: 24px;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.bg['bg-neutral-subtle'].value};
  border-radius: 8px;
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CurrentFile = styled(Typography.Body2)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
  margin-top: 8px;
`;

const AnnouncementWrapper = styled.div`
  margin-top: 16px;
`;

export function DownloadUSDocuments(): EmotionJSX.Element {
  const navigate = useNavigate();
  const { onRefreshToken } = useRefreshToken();
  const { tokens } = useGlimmerTheme();
  const { APOLLO_SERVER } = useEnvVariables();
  const baseUrl = APOLLO_SERVER.endsWith('/')
    ? APOLLO_SERVER.slice(0, -1)
    : APOLLO_SERVER;

  const [formState, setFormState] = useState<FormState>({
    selectedGrade: '',
    documentType: '',
    downloadType: 'separate',
    hideStandards: false,
    language: 'en',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'info' | 'error' | 'warning';
    text: string;
  } | null>(null);
  const [failedDownloads, setFailedDownloads] = useState<FailedDownload[]>([]);
  const [downloadProgress, setDownloadProgress] =
    useState<DownloadProgress | null>(null);
  const [totalFiles, setTotalFiles] = useState<number>(0);

  const handleInputChange = (
    field: keyof FormState,
    value: string | boolean
  ): void => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));

    setNotification(null);
    setFailedDownloads([]);
    setDownloadProgress(null);
    setTotalFiles(0);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setNotification(null);
    setFailedDownloads([]);
    setDownloadProgress(null);

    try {
      const documentType =
        formState.documentType === 'personalized-practice'
          ? 'print-us-modules'
          : 'print-pacing-guide';

      const params: DocumentParams = {
        courseId: formState.selectedGrade,
        documentType: documentType,
        ...(formState.documentType === 'personalized-practice' && {
          hideStandards: formState.hideStandards.toString(),
        }),
      };

      if (formState.downloadType === 'single') {
        const redirectPath = `/${documentType}/${formState.selectedGrade}`;
        const queryParams =
          formState.documentType === 'personalized-practice' &&
          formState.hideStandards
            ? '?hideStandards=true'
            : '';

        navigate(`${redirectPath}${queryParams}`);
        return;
      }

      if (formState.downloadType === 'separate') {
        const total =
          documentType === 'print-us-modules'
            ? NUMBER_OF_STRETCHES * NUMBER_OF_MODULES
            : NUMBER_OF_STRETCHES;
        setTotalFiles(total);
        setDownloadProgress({
          current: 0,
          total,
          fileName: '',
        });
        await handleSeparateDownload(documentType, params);
        return;
      }
    } catch (error) {
      console.error('Error downloading documents:', error);
      setNotification({
        type: 'error',
        text: 'Failed to download documents. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeparateDownload = async (
    documentType: string,
    params: DocumentParams
  ): Promise<void> => {
    await new Promise<void>((resolve) => {
      onRefreshToken(async () => {
        try {
          const result = await downloadSeparateFiles(
            documentType,
            params,
            formState.selectedGrade,
            baseUrl,
            setDownloadProgress
          );

          const processedResult = processDownloadResult(
            result,
            params,
            documentType
          );

          setFailedDownloads(processedResult.failedDownloads);
          if (processedResult.notification) {
            setNotification(processedResult.notification);
          }
        } catch (error) {
          console.error('Error downloading documents:', error);
          setNotification({
            type: 'error',
            text: 'Failed to download documents. Please try again.',
          });
        } finally {
          resolve();
        }
      });
    });
  };

  return (
    <Container>
      <Typography.H2>Download US Documents</Typography.H2>
      {notification && (
        <StyledAnnouncementPanel
          type={notification.type}
          text={notification.text}
          canClose
        />
      )}
      <Form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <SelectContainer>
            <Select
              value={formState.selectedGrade}
              onChange={(option) =>
                handleInputChange('selectedGrade', option?.value ?? '')
              }
              options={gradeOptions}
              placeholder="Select a grade"
              labelText="Grade"
              descriptiveText={
                !formState.selectedGrade
                  ? 'Please select a grade to download documents'
                  : undefined
              }
            />
          </SelectContainer>

          <SelectRow>
            <SelectContainer>
              <Select
                value={formState.documentType}
                onChange={(option) =>
                  handleInputChange(
                    'documentType',
                    option?.value as DocumentType
                  )
                }
                options={documentTypeOptions}
                labelText="Document Type"
                placeholder="Select a document type"
                descriptiveText={
                  !formState.documentType
                    ? 'Please select a document type to download documents'
                    : undefined
                }
              />
            </SelectContainer>

            <SelectContainer>
              <Select
                value={formState.language}
                onChange={(option) =>
                  handleInputChange('language', option?.value as Language)
                }
                options={languageOptions}
                labelText="Language"
              />
            </SelectContainer>
          </SelectRow>

          {formState.documentType === 'personalized-practice' && (
            <SelectContainer>
              <Select
                value={formState.hideStandards.toString()}
                onChange={(option) =>
                  handleInputChange('hideStandards', option?.value === 'true')
                }
                options={standardsOptions}
                labelText="Standards Visibility"
              />
            </SelectContainer>
          )}

          <SelectContainer>
            <Select
              value={formState.downloadType}
              onChange={(option) =>
                handleInputChange('downloadType', option?.value as DownloadType)
              }
              options={downloadTypeOptions}
              labelText="Download Type"
            />
          </SelectContainer>

          <Button
            type="submit"
            disabled={
              isLoading || !formState.selectedGrade || !formState.documentType
            }
            variant="accent"
            fill
          >
            {isLoading ? 'Downloading...' : 'Download Documents'}
          </Button>
        </div>
      </Form>
      {isLoading && formState.downloadType === 'separate' && (
        <ProgressContainer>
          <ProgressInfo>
            <Typography.Body1>
              {`Downloading files (${
                downloadProgress?.current || 0
              } of ${totalFiles})`}
            </Typography.Body1>
            <Typography.Body1>
              {downloadProgress?.current
                ? Math.round((downloadProgress.current / totalFiles) * 100)
                : 0}
              %
            </Typography.Body1>
          </ProgressInfo>
          <Bar
            width="100%"
            colors={[tokens.color.alias.cm.icon['icon-accent'].value]}
            values={[
              downloadProgress?.current
                ? (downloadProgress.current / totalFiles) * 100
                : 1,
            ]}
          />
          <CurrentFile>
            {downloadProgress?.fileName
              ? `Last downloaded file: ${downloadProgress.fileName}`
              : 'Preparing download...'}
          </CurrentFile>
          <AnnouncementWrapper>
            <AnnouncementPanel
              type="info"
              text="Please do not refresh the page while downloading. You can switch to other tabs if needed."
            />
          </AnnouncementWrapper>
        </ProgressContainer>
      )}
      {failedDownloads.length > 0 && (
        <FailedDownloads failedDownloads={failedDownloads} />
      )}
    </Container>
  );
}
